/**
 * Fusion performances — Chenda Melam with other instruments & styles.
 */
import { CONTACT } from "./contactInfo";

export const fusionsIntro = {
  eyebrow: "Praana Beats",
  title: "Chenda Melam Fusion in Chennai",
  subtitle:
    "Kerala Chenda Melam meets violin, flute, keys, and contemporary stage energy — Praana Beats for weddings, cultural nights, and live collaborations across Chennai and South India.",
  note:
    "Our fusion work always starts from authentic Kerala rhythm. We blend carefully so the Chenda Melam identity stays strong — whether you want a Telugu–Kerala wedding procession, a Carnatic crossover concert, or a corporate cultural showcase.",
};

export const fusionStyles = [
  {
    id: "carnatic-fusion",
    title: "Chenda Melam × Carnatic Percussion",
    tag: "Classical Crossover",
    summary:
      "Chenda Melam layered with Mridangam, Ghatam, Kanjira, and Morsing for sabha-style and wedding-stage presentations.",
    instruments: ["Chenda", "Mridangam", "Kanjira", "Ghatam", "Ilathalam"],
    occasions: ["Wedding receptions", "Cultural sabhas", "Arangetrams", "Temple stages"],
    details:
      "We design talam conversations between Kerala Melam patterns and Carnatic rhythmic frameworks — powerful Valanthala strokes with Mridangam korvais, shared nadai explorations, and ensemble climaxes that feel grand without losing classical discipline.",
  },
  {
    id: "parai-folk",
    title: "Chenda Melam × Parai & Folk Drums",
    tag: "Folk Energy",
    summary:
      "High-energy processions combining Chenda Melam with Parai, Thappu, and Tamil folk percussion lines.",
    instruments: ["Chenda", "Parai", "Thappu", "Urumee", "Ilathalam"],
    occasions: ["Street processions", "Groom entry", "Temple car festivals", "Community events"],
    details:
      "Ideal when you want festive street energy with a Kerala core. Chenda Melam leads the melodic rhythm while Parai and folk drums add regional colour — popular for Chennai baraats, village-style weddings, and outdoor celebrations.",
  },
  {
    id: "panchavadyam-blend",
    title: "Panchavadyam × Melam Fusion Sets",
    tag: "Temple Heritage",
    summary:
      "Compact sets that weave Panchavadyam colours into wedding-friendly Melam formats.",
    instruments: ["Chenda", "Thimila", "Maddalam", "Idakka", "Kombu", "Ilathalam"],
    occasions: ["Muhurtham", "Temple weddings", "Griha pravesh", "Festival evenings"],
    details:
      "For families who want sacred Kerala texture beyond a standard wedding band. We shape shorter, stage-friendly suites from Melam and Panchavadyam vocabulary — elegant, auspicious, and photographically striking.",
  },
  {
    id: "south-india-wedding",
    title: "South Indian Wedding Fusion Melam",
    tag: "Wedding Special",
    summary:
      "Kerala Chenda Melam tailored for Tamil, Telugu, and Kannada wedding rituals and guest experiences.",
    instruments: ["Chenda", "Ilathalam", "Kombu", "Kurumkuzhal", "Optional Parai / Mridangam"],
    occasions: ["Groom entry", "Baraat", "Reception entry", "Sangeet nights"],
    details:
      "Our most requested fusion format in Chennai, Hyderabad, and Vijayawada. We keep Melam timing flexible for venue cues, photographer moments, and family rituals — while delivering the signature Kerala thunder guests remember.",
  },
  {
    id: "contemporary-stage",
    title: "Contemporary & Stage Collaborations",
    tag: "Modern Stage",
    summary:
      "Directed collaborations with composers, dance troupes, and live bands for shows and inaugurations.",
    instruments: ["Chenda Melam ensemble", "Guest percussion", "Optional keys / pads"],
    occasions: ["Corporate launches", "College fests", "Theatre", "Music festivals"],
    details:
      "We work with directors and music producers to score custom openings, walk-in themes, and finale bursts. Traditional stick technique and Melam structure stay intact — arranged for modern PA systems and timed cues.",
  },
  {
    id: "online-workshop",
    title: "Fusion Rhythm Workshops",
    tag: "Learn",
    summary:
      "Online and offline workshops exploring how Chenda Melam dialogues with other percussion traditions.",
    instruments: ["Chenda", "Mridangam concepts", "Clap / konnakol drills"],
    occasions: ["Student batches", "Artist labs", "School / college modules"],
    details:
      "Students learn respectful collaboration: listening across traditions, matching tempo, and building short fusion pieces. Available for Chennai studio sessions and global online learners.",
  },
];

export const fusionHighlights = [
  {
    title: "Rooted in tradition",
    text: "Every fusion set begins with authentic Chenda Melam technique — Edamthala, Valanthala, and classical talam cycles.",
  },
  {
    title: "Custom for your event",
    text: "Team size, duration, and guest instruments are planned around your venue, guest count, and cultural mix.",
  },
  {
    title: "Wedding-ready staging",
    text: "Cue-based entries for groom/bride processions, reception walk-ins, and muhurtham moments in Chennai venues.",
  },
  {
    title: "South India travel",
    text: "Fusion Melam available across Tamil Nadu, Andhra Pradesh, and Telangana — including Hyderabad and Vijayawada weddings.",
  },
];

export const fusionFaqs = [
  {
    q: "What is Chenda Melam fusion in Chennai?",
    a: "It is a performance format where Kerala Chenda Melam is thoughtfully combined with other instruments — such as Mridangam, Parai, or Kanjira — for weddings and cultural events, while keeping traditional Melam identity intact.",
  },
  {
    q: "Can you do fusion for a Telugu–Kerala wedding?",
    a: "Yes. South Indian wedding fusion Melam is one of our specialties — groom entry, baraat, and reception sets designed for mixed-tradition celebrations in Chennai, Hyderabad, and beyond.",
  },
  {
    q: "Do you provide only pure traditional Melam as well?",
    a: "Absolutely. Fusion is optional. Many families book pure Kerala Chenda Melam. Share your preference when you enquire and we will recommend the right format.",
  },
  {
    q: "How do I book a fusion Chenda Melam team?",
    a: `WhatsApp or call ${CONTACT.phonesDisplay} with your date, venue area in Chennai (or city), and the instruments or style you want. We will confirm team size and pricing quickly.`,
  },
];
